// Reset-password — consume the token (URL-safe random from email or
// 6-digit OTP from SMS) and set a new password. Pre-fills `token` from
// query string when the user clicked an email link.

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, merchantAuth } from '@/api/client';
import { Logo } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/m/reset-password')({
  component: MerchantResetPassword,
});

function MerchantResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [merchantCode, setMerchantCode] = useState('');
  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  // Pre-fill from `?token=...` (email link path).
  useEffect(() => {
    const u = new URL(window.location.href);
    const t = u.searchParams.get('token');
    if (t) setToken(t);
  }, []);

  const submit = useMutation({
    mutationFn: () =>
      merchantAuth.resetPassword({
        merchant_code: merchantCode,
        username,
        token,
        new_password: newPassword,
      }),
    onSuccess: () => {
      toast.success(
        t('merchant.reset_password.ok', {
          defaultValue: 'Password reset. You can now sign in.',
        }),
      );
      navigate({ to: '/m/login' });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : (t('auth.invalid_credentials') as string),
      ),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast.error(t('merchant.password.mismatch', { defaultValue: 'Passwords do not match' }));
      return;
    }
    submit.mutate();
  };

  return (
    <main className="auth-backdrop min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-sm shadow-elev">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <Logo size="xl" className="drop-shadow-sm" />
          <div className="space-y-1">
            <CardTitle className="text-xl">
              {t('merchant.reset_password.title', { defaultValue: 'Set a new password' })}
            </CardTitle>
            <CardDescription>
              {t('merchant.reset_password.subtitle', {
                defaultValue:
                  'Enter the token from your email or the OTP from your SMS, plus your new password.',
              })}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="merchant">{t('merchants.code')}</Label>
              <Input
                id="merchant"
                required
                autoFocus
                value={merchantCode}
                onChange={(e) => setMerchantCode(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="username">{t('auth.username')}</Label>
              <Input
                id="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="token">
                {t('merchant.reset_password.token', { defaultValue: 'Reset token / OTP' })}
              </Label>
              <Input
                id="token"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np">
                {t('merchant.password.new', { defaultValue: 'New password (min 12)' })}
              </Label>
              <Input
                id="np"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp">
                {t('merchant.password.confirm', { defaultValue: 'Confirm new password' })}
              </Label>
              <Input
                id="cp"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={submit.isPending}>
              {t('merchant.reset_password.submit', { defaultValue: 'Reset password' })}
            </Button>
            <div className="text-center text-xs text-muted-foreground pt-1">
              <Link to="/m/login" className="underline hover:text-foreground">
                {t('merchant.forgot_password.back_to_login', {
                  defaultValue: 'Back to sign in',
                })}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
